import { useCallback, useEffect, useRef, useState } from 'react';

import {
  COT_EVENT_TYPES,
  streamAgentRunEvents,
  type AgentWorkflowEvent,
} from '@/services/agents/run-events-stream';

export const MAX_RETAINED_EVENTS = 400;
const MAX_CONSECUTIVE_FAILURES = 5;
const RECONNECT_DELAY_MS = 2_000;

interface RunSubscription {
  controller: AbortController;
  lastSequence: number;
  done: boolean;
}

export function useAgentRunEvents({
  orgId,
  agentId,
}: {
  orgId?: string;
  agentId?: string;
}) {
  const [events, setEvents] = useState<AgentWorkflowEvent[]>([]);
  const [activeRunCount, setActiveRunCount] = useState(0);
  const subscriptionsRef = useRef<Map<string, RunSubscription>>(new Map());

  const appendEvent = useCallback((event: AgentWorkflowEvent) => {
    setEvents((prev) =>
      prev.length >= MAX_RETAINED_EVENTS
        ? [...prev.slice(1), event]
        : [...prev, event],
    );
  }, []);

  const finishRun = useCallback((runId: string) => {
    const sub = subscriptionsRef.current.get(runId);
    if (sub) sub.done = true;
    setActiveRunCount(
      [...subscriptionsRef.current.values()].filter((s) => !s.done).length,
    );
  }, []);

  const watchRun = useCallback(
    (runId: string) => {
      if (!orgId || !agentId || !runId) return;
      if (subscriptionsRef.current.has(runId)) return;

      const controller = new AbortController();
      const sub: RunSubscription = {
        controller,
        lastSequence: 0,
        done: false,
      };
      subscriptionsRef.current.set(runId, sub);
      setActiveRunCount(
        [...subscriptionsRef.current.values()].filter((s) => !s.done).length,
      );

      const subscribe = async () => {
        let failures = 0;
        // The CoT filter is dropped after a pre-event failure in case the
        // upstream gateway predates the `types` parameter.
        let useTypesFilter = true;

        while (!sub.done && !controller.signal.aborted) {
          let receivedEvent = false;
          await streamAgentRunEvents({
            orgId,
            agentId,
            runId,
            sinceSequence: sub.lastSequence,
            types: useTypesFilter ? COT_EVENT_TYPES : undefined,
            signal: controller.signal,
            onEvent: (event) => {
              receivedEvent = true;
              failures = 0;
              if (typeof event.sequence === 'number') {
                sub.lastSequence = Math.max(sub.lastSequence, event.sequence);
              }
              appendEvent(event);
            },
            onEnd: () => {
              sub.done = true;
            },
            onError: () => {
              if (!receivedEvent && useTypesFilter) {
                useTypesFilter = false;
              }
              failures += 1;
            },
          });

          if (sub.done || controller.signal.aborted) break;
          if (failures >= MAX_CONSECUTIVE_FAILURES) {
            sub.done = true;
            break;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, RECONNECT_DELAY_MS),
          );
        }

        finishRun(runId);
      };

      void subscribe();
    },
    [orgId, agentId, appendEvent, finishRun],
  );

  const reset = useCallback(() => {
    for (const sub of subscriptionsRef.current.values()) {
      sub.done = true;
      sub.controller.abort();
    }
    subscriptionsRef.current.clear();
    setActiveRunCount(0);
    setEvents([]);
  }, []);

  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    return () => {
      for (const sub of subscriptions.values()) {
        sub.done = true;
        sub.controller.abort();
      }
      subscriptions.clear();
    };
  }, []);

  return { events, activeRunCount, watchRun, reset };
}
